import { createSlice, createAsyncThunk, createSelector } from '@reduxjs/toolkit';
import { get, ref, push, set, remove } from 'firebase/database';
import { ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../common/firebase';

// Helper function to create notifications
const createNotification = async (notificationData) => {
    const notificationsRef = ref(db, 'notifications');
    const newNotificationRef = push(notificationsRef);
    await set(newNotificationRef, notificationData);
};

const uploadImage = async (file) => {
    const filename = `${Date.now()}_${file.name}`;
    const imageRef = storageRef(storage, `thoughts_images/${filename}`);
    await uploadBytes(imageRef, file);
    const imageUrl = await getDownloadURL(imageRef); // Get the actual URL
    return imageUrl; // Return the URL
};

// Async thunk for fetching thoughts from Firebase
export const fetchthoughts = createAsyncThunk('thoughts/fetchthoughts', async () => {
    const thoughtsRef = ref(db, 'thoughts');
    const snapshot = await get(thoughtsRef);
    const thoughts = snapshot.val() || {};
    return Object.keys(thoughts).map(key => ({ 
        id: key, 
        ...thoughts[key],
        createdAt: Date.now(),
        lastUpdated: Number(thoughts[key].lastUpdated) || Date.now()
    }));
});

// Async thunk for adding a new thought
export const addthought = createAsyncThunk('thoughts/addthought', async (thoughtData) => {
    const timestamp = Date.now();
    const newthoughtData = {
        ...thoughtData,
        createdAt: timestamp,
        lastUpdated: timestamp,
        likes: {},
        comments: {},
    };
    // if (thoughtData.profileImage instanceof File) {   
    //     const imageUrl = await uploadImage(thoughtData.profileImage); 
    //     newthoughtData.profileImage = imageUrl;  
    // }

    const thoughtsRef = ref(db, 'thoughts');
    const newthoughtRef = push(thoughtsRef);
    await set(newthoughtRef, newthoughtData);

    // Create a notification
    await createNotification({
        type: thoughtData.type,
        contentId: newthoughtRef.key,
        title: newthoughtData.title,
        description: `A new thought titled "${newthoughtData.title}" has been added.`,
        timestamp: timestamp,
    });

    return { id: newthoughtRef.key, ...newthoughtData };
});

// Async thunk for updating an existing thought
export const updatethought = createAsyncThunk('thoughts/updatethought', async ({ id, thoughtData }) => {
    const updatedthoughtData = {
        ...thoughtData,
        lastUpdated: Date.now()
    };

    // if (thoughtData.profileImage instanceof File) {
    //     const imageUrl = await uploadImage(thoughtData.profileImage);
    //     updatedthoughtData.profileImage = imageUrl;  
    // }
    const thoughtRef = ref(db, `thoughts/${id}`);
    await set(thoughtRef, updatedthoughtData);
    return { id, ...updatedthoughtData };
});


// Selectors
export const selectAllthoughts = state => state.thoughts.thoughts;

export const selectLatestthoughts = createSelector(
    [selectAllthoughts],
    (thoughts) => {
        return [...thoughts]
            .sort((a, b) => Number(b.createdAt) - Number(a.createdAt))
            .slice(0, 3);
    }
);

export const selectMostLikedthoughts = createSelector(
    [selectAllthoughts],
    (thoughts) => {
        return [...thoughts]
            .sort((a, b) => {
                const likesA = a.likes ? Object.keys(a.likes).length : 0;
                const likesB = b.likes ? Object.keys(b.likes).length : 0;
                return likesB - likesA;
            })
            .slice(0, 3);
    }
);

// Async thunk for deleting a thought
export const deletethought = createAsyncThunk('thoughts/deletethought', async (id) => {
    const thoughtRef = ref(db, `thoughts/${id}`);
    await remove(thoughtRef);
    return id;
});

// New async thunk for adding a like to a thought
export const addLike = createAsyncThunk('thoughts/addLike', async ({ thoughtId, userName }) => {
    const likeRef = ref(db, `thoughts/${thoughtId}/likes/${userName}`);
    await set(likeRef, true);
    return { thoughtId, userName };
});

// New async thunk for removing a like from a thought
export const removeLike = createAsyncThunk('thoughts/removeLike', async ({ thoughtId, userName }) => {
    const likeRef = ref(db, `thoughts/${thoughtId}/likes/${userName}`);
    await remove(likeRef);
    return { thoughtId, userName };
});

// New async thunk for adding a comment to a thought
export const addComment = createAsyncThunk('thoughts/addComment', async ({ thoughtId, userName, comment }) => {
    const commentsRef = ref(db, `thoughts/${thoughtId}/comments`);
    const newCommentRef = push(commentsRef);
    const timestamp = Date.now();
    const commentData = {
        userName,
        text: comment,
        timestamp: timestamp,
    };
    await set(newCommentRef, commentData);

    // Create a notification
    await createNotification({
        type: 'newComment',
        contentId: thoughtId,
        title: `New comment by ${userName}`,
        description: `"${userName}" commented on a thought: "${comment}"`,
        timestamp: timestamp,
    });

    return { thoughtId, commentId: newCommentRef.key, ...commentData };
});

// Rest of the async thunks remain the same...

const thoughtSlice = createSlice({
    name: 'thought',
    initialState: {
        thoughts: [],
        loading: false,
        error: null,
    },
    reducers: {},
    extraReducers: (builder) => {
        builder
            .addCase(fetchthoughts.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchthoughts.fulfilled, (state, action) => {
                state.loading = false;
                state.thoughts = action.payload;
                state.error = null;
            })
            .addCase(fetchthoughts.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error.message;
            })
            .addCase(addthought.fulfilled, (state, action) => {
                state.thoughts.push(action.payload);
            })
            .addCase(updatethought.fulfilled, (state, action) => {
                const index = state.thoughts.findIndex(thought => thought.id === action.payload.id);
                if (index !== -1) {
                    state.thoughts[index] = action.payload;
                }
            })
            .addCase(deletethought.fulfilled, (state, action) => {
                state.thoughts = state.thoughts.filter(thought => thought.id !== action.payload);
            })
            .addCase(addLike.fulfilled, (state, action) => {
                const { thoughtId, userName } = action.payload;
                const thought = state.thoughts.find(p => p.id === thoughtId);
                if (thought) {
                    if (!thought.likes) thought.likes = {};
                    thought.likes[userName] = true;
                }
            })
            .addCase(removeLike.fulfilled, (state, action) => {
                const { thoughtId, userName } = action.payload;
                const thought = state.thoughts.find(p => p.id === thoughtId);
                if (thought && thought.likes) {
                    delete thought.likes[userName];
                }
            })
            .addCase(addComment.fulfilled, (state, action) => {
                const { thoughtId, commentId, ...commentData } = action.payload;
                const thought = state.thoughts.find(p => p.id === thoughtId);
                if (thought) {
                    if (!thought.comments) thought.comments = {};
                    thought.comments[commentId] = commentData;
                }
            });
    },
});

export default thoughtSlice.reducer;
